import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Layers, Hammer, Gem, Compass, Brush, Mountain, PaintBucket, Star } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Label } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { SERVICES } from "../data/services";
import { ARTICLES } from "../data/articles";

const SITE_URL = "https://resineko.com";

const FAQ_HOME = [
  { q: "Resineko intervient-elle en dehors de Toulouse ?", a: "Oui. Basés à Daux (31700), près de Toulouse, nous intervenons dans toute la Haute-Garonne, en Occitanie et partout en France — nos chantiers sont organisés par secteur pour optimiser les déplacements." },
  { q: "Comment obtenir un devis pour mon sol ?", a: "Par téléphone au 07 83 69 31 01 (7h30 – 18h30) ou via le formulaire en ligne : décrivez votre projet avec quelques photos, nous vous répondons rapidement." },
  { q: "Quels revêtements propose Resineko ?", a: "Six savoir-faire : résine décorative, résine industrielle, résine pailletée, béton ciré, moquette de pierre (tapis de pierre) et peinture en bâtiment — pour les particuliers, les professionnels et les industriels." },
  { q: "Résine ou béton ciré : comment choisir ?", a: "Tout dépend de l'usage, du support et du rendu souhaité. Nous étudions votre projet et, si nécessaire, réalisons une visite technique pour vous orienter vers le système adapté — jamais l'inverse." },
];

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: "Resineko",
      url: SITE_URL,
      telephone: "+33783693101",
      description: "Spécialiste des revêtements de sols en résine, du béton ciré, de la moquette de pierre et de la peinture en bâtiment. Basée à Daux, près de Toulouse, intervention partout en France.",
      areaServed: "France",
    },
    {
      "@type": "LocalBusiness",
      name: "Resineko",
      url: SITE_URL,
      telephone: "+33783693101",
      email: "resineko31@gmail.com",
      image: `${SITE_URL}/images/og-image.jpg`,
      description: "Revêtements de sols en résine, béton ciré, moquette de pierre et peinture en bâtiment à Toulouse et partout en France.",
      sameAs: ["https://share.google/oB26YWKfBF2VXP4ye"],
      address: {
        "@type": "PostalAddress",
        streetAddress: "67 Rue de la République",
        addressLocality: "Daux",
        postalCode: "31700",
        addressRegion: "Occitanie",
        addressCountry: "FR",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "18",
      },
      openingHours: "Mo-Sa 07:30-18:30",
      areaServed: [
        { "@type": "City", name: "Daux" },
        { "@type": "City", name: "Toulouse" },
        { "@type": "City", name: "Bordeaux" },
        { "@type": "City", name: "Montpellier" },
        { "@type": "City", name: "Lyon" },
        { "@type": "City", name: "Nantes" },
        { "@type": "City", name: "Lille" },
        { "@type": "City", name: "Nice" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ_HOME.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ],
};

const SERVICE_ICONS = {
  "resine-decorative": Gem,
  "resine-industrielle": Layers,
  "resine-pailletee": Brush,
  "beton-cire": Hammer,
  "moquette-de-pierre": Mountain,
  "peinture-batiment": PaintBucket,
};

const BENTO = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

const POURQUOI = [
  { n: "01", title: "Expertise technique", text: "Une connaissance approfondie des systèmes résine, béton ciré et moquette de pierre, au service de chaque projet." },
  { n: "02", title: "Solutions adaptées", text: "Le système est choisi selon votre usage réel — jamais l'inverse. Chaque projet reçoit la réponse technique qu'il mérite." },
  { n: "03", title: "Préparation rigoureuse", text: "Diagnostic, réparation, ponçage : la préparation des supports est la condition invisible de la durabilité." },
  { n: "04", title: "Finitions soignées", text: "Détails nets, raccords propres, protection des espaces : l'exigence du geste jusqu'au dernier mètre." },
  { n: "05", title: "Accompagnement personnalisé", text: "Un interlocuteur attentif, du premier échange à la réception du chantier." },
  { n: "06", title: "Intervention nationale", text: "Basés dans la région toulousaine, nous nous déplaçons partout en France." },
];

const TESTIMONIALS = [
  {
    name: "La Baguette Occitane",
    context: "Boulangerie — sol industriel époxy",
    text: "Très satisfaits de notre sol en résine époxy industrielle : le résultat est impeccable. Le sol est esthétique, résistant et surtout très facile à nettoyer. Un travail minutieux, nous recommandons vivement.",
  },
  {
    name: "Olivier F.",
    context: "Terrasse extérieure en résine pailletée",
    text: "Très satisfait du travail réalisé par Resineko : un sol en résine pailletée sur ma terrasse extérieure, et le résultat est tout simplement impeccable. Une entreprise professionnelle du début à la fin, avec des conseils pertinents et un travail soigné.",
  },
  {
    name: "Tej B.",
    context: "Garage en résine époxy",
    text: "J'ai fait appel à Resineko pour la pose d'un sol en résine époxy dans mon garage. Le sol est facile à nettoyer et très résistant. Une entreprise professionnelle et fiable, je recommande.",
  },
];

const ETAPES = [
  { n: "01", title: "Vous décrivez votre projet", text: "Via le formulaire de devis : nature des lieux, surface, envies. Deux minutes suffisent." },
  { n: "02", title: "Nous étudions ensemble", text: "Échange, conseils et, si nécessaire, visite technique pour diagnostiquer le support." },
  { n: "03", title: "Devis détaillé", text: "Une proposition claire : système recommandé, étapes, planning. Sans surprise." },
  { n: "04", title: "Réalisation soignée", text: "Préparation, application, finition : un chantier mené avec rigueur, jusqu'à la réception." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <Seo
        title="Sol en résine époxy, béton ciré & moquette de pierre à Toulouse | Resineko"
        description="Spécialiste des sols en résine, béton ciré, moquette de pierre et peinture en bâtiment. Basés à Daux, nous intervenons à Toulouse et partout en France. Devis gratuit : 07 83 69 31 01."
        path="/"
        jsonLd={ORG_JSONLD}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden" data-testid="home-hero">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <motion.img
            src="/images/hero-main.jpg"
            alt="Salon contemporain : sol en béton ciré et sol en résine époxy à effet métallique — Resineko"
            className="h-full w-full object-cover object-right lg:object-center"
            loading="eager"
            initial={{ scale: 1.06, opacity: 0.5 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
          <div className="absolute inset-0 bg-[#0A0A0A]/45 lg:bg-transparent" />
          <div className="absolute inset-0 hidden bg-gradient-to-l from-[#0A0A0A]/80 via-[#0A0A0A]/25 to-transparent lg:block" />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{ background: "linear-gradient(90deg, transparent 28%, rgba(10,10,10,0.6) 46%, rgba(10,10,10,0.6) 74%, transparent 90%)" }}
          />
          <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#0A0A0A]/60 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-10 md:pb-32 lg:flex lg:flex-col lg:items-end lg:text-right">
          <h1 className="font-serif text-4xl font-light leading-[1.02] text-[#F4F4F0] sm:text-5xl lg:text-7xl" data-testid="hero-h1">
            <MaskedLine delay={0.35}>Revêtements de sols</MaskedLine>
            <MaskedLine delay={0.48}>en résine époxy, <em className="not-italic text-[#2FBF8F]">béton ciré</em></MaskedLine>
            <MaskedLine delay={0.61}>&amp; moquette de pierre</MaskedLine>
          </h1>
          <span className="block overflow-hidden">
            <motion.p
              className="mt-8 max-w-xl text-base leading-relaxed text-[#F4F4F0]/75 md:text-lg"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              Resineko accompagne particuliers, professionnels et industriels dans leurs projets de revêtements de sols et de décoration, partout en France.
            </motion.p>
          </span>
          <motion.div
            className="mt-10 flex flex-col gap-4 sm:flex-row"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/devis"
              data-testid="hero-devis-cta"
              className="group relative inline-flex items-center justify-center overflow-hidden bg-[#047857] px-8 py-4 text-xs font-medium uppercase tracking-[0.22em] text-white"
            >
              <span className="absolute inset-0 -translate-x-full bg-[#065F46] transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative flex items-center gap-3">Demander un devis <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" /></span>
            </Link>
            <Link
              to="/realisations"
              data-testid="hero-realisations-cta"
              className="group inline-flex items-center justify-center gap-3 border border-white/25 px-8 py-4 text-xs font-medium uppercase tracking-[0.22em] text-[#F4F4F0] backdrop-blur-sm transition-colors duration-500 hover:border-[#F4F4F0]"
            >
              Voir nos réalisations <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
          <motion.p
            className="mt-10 flex items-center gap-2.5 text-xs uppercase tracking-[0.22em] text-[#F4F4F0]/55"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            data-testid="hero-location-line"
          >
            <MapPin size={13} className="text-[#2FBF8F]" />
            Basés à Daux (31), région toulousaine — Intervention partout en France
          </motion.p>
        </motion.div>

        <motion.div
          className="absolute bottom-8 right-8 hidden flex-col items-center gap-3 md:flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4F4F0]/40" style={{ writingMode: "vertical-rl" }}>Défiler</span>
          <motion.span
            className="h-14 w-px bg-gradient-to-b from-[#2FBF8F] to-transparent"
            animate={{ scaleY: [1, 0.4, 1] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </motion.div>
      </section>

      <Marquee items={["Résine décorative", "Résine industrielle", "Résine pailletée", "Béton ciré", "Moquette de pierre", "Peinture bâtiment"]} />

      {/* MANIFESTO */}
      <section className="noise-bg relative bg-[#0A0A0A]">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 md:grid-cols-12 md:px-10 md:py-36">
          <div className="md:col-span-5">
            <Reveal><Label>Le savoir-faire Resineko</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
                Un sol n'est pas une surface.<br />
                <em className="text-[#2FBF8F]">C'est une architecture.</em>
              </h2>
            </Reveal>
          </div>
          <div className="space-y-10 md:col-span-6 md:col-start-7">
            {[
              { n: "01", title: "La matière avant tout", text: "Résine époxy, béton ciré, granulats de pierre : nous travaillons des matériaux exigeants, choisis pour leur tenue comme pour leur beauté." },
              { n: "02", title: "Le geste qui fait la différence", text: "Un revêtement d'exception se joue dans la préparation et l'application. Diagnostic, ponçage, couches successives : rien n'est laissé au hasard." },
              { n: "03", title: "Un projet, une réponse", text: "Particulier, commerce, industrie : chaque cahier des charges est unique. Nous recommandons le système adapté à votre usage — jamais l'inverse." },
            ].map((item, i) => (
              <Reveal key={item.n} delay={i * 0.08}>
                <div className="group flex gap-6 border-t border-white/10 pt-8" data-testid={`manifesto-${item.n}`}>
                  <span className="font-serif text-2xl italic text-[#047857]">{item.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl font-light text-[#F4F4F0]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#8C8C8C]">{item.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-[#0e0e0e] py-24 md:py-36" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal><Label>Nos solutions</Label></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
                  Six savoir-faire, une même exigence
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <p className="max-w-sm text-sm leading-relaxed text-[#8C8C8C]">
                Du sol décoratif d'une maison au revêtement technique d'une usine, chaque solution trouve sa place.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-4 md:grid-cols-12">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.slug];
              return (
                <Reveal key={s.slug} delay={i * 0.05} className={`${BENTO[i]} col-span-1`}>
                  <Link
                    to={`/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group relative flex h-full min-h-[320px] flex-col justify-end overflow-hidden border border-white/10 bg-[#111]"
                  >
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-60 transition-[opacity,transform] duration-700 ease-out group-hover:scale-105 group-hover:opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                    <div className="relative p-7 md:p-9">
                      <Icon size={20} className="text-[#2FBF8F]" />
                      <h3 className="mt-4 font-serif text-2xl font-light text-[#F4F4F0] md:text-3xl">{s.name}</h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-[#F4F4F0]/65">{s.tagline}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#2FBF8F] opacity-0 transition-all duration-500 group-hover:opacity-100">
                        Découvrir <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                    <span className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-[#047857] transition-transform duration-500 ease-out group-hover:scale-x-100" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="noise-bg bg-[#F4F4F0] py-24 text-[#0A0A0A] md:py-36" data-testid="pourquoi-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label light>Pourquoi choisir Resineko ?</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] md:text-5xl">
              L'exigence d'un chantier bien mené, du premier échange à la dernière finition.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
            {POURQUOI.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05}>
                <div className="group h-full bg-[#F4F4F0] p-8 transition-colors duration-500 hover:bg-[#ecece6] md:p-10" data-testid={`pourquoi-${p.n}`}>
                  <span className="font-serif text-lg italic text-[#047857]">{p.n}</span>
                  <h3 className="mt-4 font-serif text-2xl font-normal">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#0A0A0A]/60">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AVIS GOOGLE */}
      <section className="border-y border-[#047857]/30 bg-[#0d1512] py-12" data-testid="avis-section">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-5 text-center md:flex-row md:justify-between md:px-10 md:text-left">
          <Reveal>
            <div className="flex items-center gap-5">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={22} className="fill-[#d4af6a] text-[#d4af6a]" />
                ))}
              </div>
              <div>
                <p className="font-serif text-2xl font-light text-[#F4F4F0]">
                  5,0 / 5 <span className="text-[#8C8C8C] text-lg">— 18 avis Google</span>
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#2FBF8F]">Nos clients nous recommandent</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href="https://share.google/oB26YWKfBF2VXP4ye"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="avis-google-cta"
              className="group inline-flex items-center gap-3 border border-[#047857] px-6 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-[#F4F4F0] transition-colors duration-300 hover:bg-[#047857]"
            >
              Voir nos avis Google <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* TÉMOIGNAGES — avis Google réels */}
      <section className="bg-[#0A0A0A] py-20 md:py-28" data-testid="temoignages-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Ils nous ont fait confiance</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
              Des chantiers qui parlent d'eux-mêmes
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.07}>
                <figure className="flex h-full flex-col bg-[#0e0e0e] p-8 md:p-9" data-testid={`temoignage-${i}`}>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={14} className="fill-[#d4af6a] text-[#d4af6a]" />
                    ))}
                  </div>
                  <blockquote className="mt-5 flex-1 font-serif text-lg font-light italic leading-relaxed text-[#F4F4F0]/85">
                    « {t.text} »
                  </blockquote>
                  <figcaption className="mt-6 border-t border-white/10 pt-4">
                    <p className="text-sm font-normal text-[#F4F4F0]">{t.name}</p>
                    <p className="mt-0.5 text-xs text-[#8C8C8C]">{t.context}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#2FBF8F]">Avis Google vérifié</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Votre projet, étape par étape</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
              De l'idée au sol fini, un parcours simple
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 md:grid-cols-4">
            {ETAPES.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.08}>
                <div className="relative border-t border-white/15 pt-8" data-testid={`etape-${e.n}`}>
                  <span className="absolute -top-px left-0 h-px w-12 bg-[#2FBF8F]" />
                  <span className="font-serif text-4xl font-light italic text-[#047857]">{e.n}</span>
                  <h3 className="mt-5 font-serif text-xl font-normal text-[#F4F4F0]">{e.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#8C8C8C]">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/devis"
              data-testid="process-devis-cta"
              className="group relative mt-14 inline-flex overflow-hidden border border-[#047857] px-8 py-4 text-xs font-medium uppercase tracking-[0.22em] text-[#F4F4F0]"
            >
              <span className="absolute inset-0 -translate-x-full bg-[#047857] transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative flex items-center gap-3">Parler de mon projet <ArrowRight size={15} /></span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ZONE + BLOG TEASER */}
      <section className="bg-[#0e0e0e] py-24 md:py-36">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:grid-cols-2 md:px-10">
          <div>
            <Reveal><Label>Zone d'intervention</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
                De Toulouse à toute la France
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-[#8C8C8C] md:text-base">
                Basée dans la région toulousaine, Resineko accompagne ses clients partout en France : Occitanie, Nouvelle-Aquitaine, grandes métropoles et au-delà.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/zone-intervention" data-testid="home-zone-link" className="group mt-8 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.22em] text-[#2FBF8F]">
                Découvrir nos zones <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
          <div>
            <Reveal><Label>Le journal Resineko</Label></Reveal>
            <div className="mt-8 space-y-1">
              {ARTICLES.slice(0, 3).map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.06}>
                  <Link to={`/blog/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group flex items-baseline justify-between gap-6 border-t border-white/10 py-5 transition-colors hover:border-[#047857]/50">
                    <div>
                      <h3 className="font-serif text-xl font-light text-[#F4F4F0] transition-colors group-hover:text-[#2FBF8F]">{a.title}</h3>
                      <p className="mt-1 text-xs text-[#8C8C8C]">{a.excerpt}</p>
                    </div>
                    <ArrowUpRight size={16} className="shrink-0 text-[#8C8C8C] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#2FBF8F]" />
                  </Link>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <Link to="/blog" data-testid="home-blog-link" className="group mt-6 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.22em] text-[#2FBF8F]">
                Tous les articles <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 bg-[#0A0A0A] py-20 md:py-28" data-testid="home-faq">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal><Label>Questions fréquentes</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
              Vos questions, nos réponses
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ_HOME.map((f, i) => (
                <AccordionItem key={i} value={`hfaq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="text-left font-serif text-lg font-normal text-[#F4F4F0] hover:text-[#2FBF8F] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-[#8C8C8C]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Home;
